import { useEffect, useState, useMemo } from "react";
import { fetchPublicProfilesByEmails, fetchPublicProfilesByIds } from "../lib/data/users";
import { normalizeEmail } from "../lib/utils";

/**
 * Hook to fetch user profiles (including avatars) for a list of emails.
 * Fetches from the usersPublic collection where photoURL is stored.
 */
export function useUserProfiles(emails = []) {
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(false);

  // Normalize and dedupe emails. `normalizedKey` is the stable identity:
  // a string derived from the deduped+normalized list. `normalizedEmails`
  // (an array reference) flips identity every render even when its content
  // is unchanged, because callers commonly pass a fresh array literal.
  // useEffect must depend ONLY on the string key — including the array
  // would re-fire the effect on every render, causing setProfiles → render
  // → effect → setProfiles infinite loops (bug #6).
  const normalizedEmails = useMemo(() => {
    const unique = new Set(emails.filter(Boolean).map((email) => normalizeEmail(email)));
    return Array.from(unique);
  }, [emails]);
  const normalizedKey = useMemo(() => normalizedEmails.join("|"), [normalizedEmails]);

  useEffect(() => {
    if (normalizedEmails.length === 0) {
      setProfiles((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    const fetchProfiles = async () => {
      setLoading(true);
      try {
        const allProfiles = await fetchPublicProfilesByEmails(normalizedEmails);
        setProfiles(allProfiles);
      } catch (err) {
        console.error("Failed to fetch user profiles:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
    // normalizedKey is the canonical content identity; normalizedEmails
    // reference is intentionally excluded to prevent fresh-array re-fires.
  }, [normalizedKey]);

  // Helper to get avatar for an email
  const getAvatar = (email) => {
    if (!email) return null;
    return profiles[normalizeEmail(email)]?.photoURL || null;
  };

  // Helper to get display name for an email
  const getDisplayName = (email) => {
    if (!email) return null;
    return profiles[normalizeEmail(email)]?.displayName || null;
  };

  // Helper to enrich a list of emails with profile data
  const enrichUsers = (emailList) => {
    return (emailList || []).map((email) => {
      const key = normalizeEmail(email);
      return {
        email: email,
        uid: profiles[key]?.id || null,
        avatar: getAvatar(email),
        displayName: getDisplayName(email),
        publicIdentifier: profiles[key]?.publicIdentifier || null,
        publicIdentifierType: profiles[key]?.publicIdentifierType || null,
        qsUsername: profiles[key]?.qsUsername || null,
        discordUsername: profiles[key]?.discordUsername || null,
      };
    });
  };

  return {
    profiles,
    loading,
    getAvatar,
    getDisplayName,
    enrichUsers,
  };
}

export function useUserProfilesByIds(userIds = []) {
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(false);

  // Same string-key pattern as useUserProfiles above — see that doc-block
  // for the full rationale. `normalizedIds` array ref must not be a
  // useEffect dep or fresh-array callers cause infinite re-render loops.
  const normalizedIds = useMemo(() => {
    const unique = new Set((userIds || []).filter(Boolean));
    return Array.from(unique);
  }, [userIds]);
  const normalizedKey = useMemo(() => normalizedIds.join("|"), [normalizedIds]);

  useEffect(() => {
    if (normalizedIds.length === 0) {
      setProfiles((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }

    const fetchProfiles = async () => {
      setLoading(true);
      try {
        const allProfiles = await fetchPublicProfilesByIds(normalizedIds);
        setProfiles(allProfiles);
      } catch (err) {
        console.error("Failed to fetch user profiles by id:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see useUserProfiles above
  }, [normalizedKey]);

  return {
    profiles,
    loading,
  };
}
